import React from 'react';
import {
  BarChart3,
  Check,
  CircleOff,
  Droplets,
  ListChecks,
  Package,
  Plus,
  Recycle,
  Trash2,
} from 'lucide-react';

const inputClass =
  'w-full rounded-xl border border-primary/15 bg-card px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/55 focus:border-primary/60 focus:ring-2 focus:ring-primary/15';
const labelClass = 'mb-1.5 block text-xs font-medium text-foreground/85';
const hintClass = 'mt-1.5 text-[11px] leading-relaxed text-muted-foreground';

const HOUSEHOLD_COLOR_PRESETS = [
  '#08769A',
  '#246B75',
  '#0F3D2C',
  '#7F9988',
  '#D7B55B',
  '#A95610',
];

const getContrastColor = (color) => {
  const hex = String(color || '').replace('#', '');
  if (hex.length !== 6) return '#FFFFFF';
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  return ((red * 299) + (green * 587) + (blue * 114)) / 1000 > 150
    ? '#0B2F22'
    : '#FFFFFF';
};

function ColorPaletteField({ label, value, onChange, hint }) {
  const normalizedValue = String(value || '').toUpperCase();
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-primary/10 bg-secondary/35 p-3">
        {HOUSEHOLD_COLOR_PRESETS.map((color) => {
          const selected = normalizedValue === color;
          return (
            <button
              key={color}
              type="button"
              onClick={() => onChange(color)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full border-2 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/25 ${selected ? 'border-foreground shadow-sm' : 'border-card'}`}
              style={{ backgroundColor: color }}
              aria-label={`${color} szín kiválasztása`}
              aria-pressed={selected}
              title={color}
            >
              {selected ? <Check className="h-4 w-4" style={{ color: getContrastColor(color) }} strokeWidth={2.8} /> : null}
            </button>
          );
        })}
        <label className="ml-1 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-primary/10 bg-card px-2.5 py-1.5 text-[11px] font-medium text-foreground/80">
          <input
            type="color"
            value={normalizedValue || '#0F3D2C'}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
            aria-label={`${label} egyéni színe`}
          />
          Egyéni
        </label>
        <span className="text-[11px] font-medium tabular-nums text-muted-foreground">{normalizedValue}</span>
      </div>
      {hint ? <p className={hintClass}>{hint}</p> : null}
    </div>
  );
}

function ImpactToggle({ enabled, onChange, color, icon: Icon, title, description }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors ${enabled ? 'bg-card shadow-sm' : 'border-primary/10 bg-secondary/25 hover:bg-secondary/45'}`}
      style={enabled ? { borderColor: color } : undefined}
      aria-pressed={enabled}
    >
      <span
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${color}1F`, color }}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-primary">{title}</span>
        <span className="mt-1 block text-xs leading-5 text-muted-foreground">{description}</span>
      </span>
      <span
        className="relative h-7 w-12 shrink-0 rounded-full transition-colors"
        style={{ backgroundColor: enabled ? color : '#C8C8C0' }}
        aria-hidden="true"
      >
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
      </span>
    </button>
  );
}

function HouseholdEditorSection({ number, icon: Icon, title, description, children }) {
  return (
    <section className="rounded-2xl border border-primary/10 bg-card/75 p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-4">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#D7B55B] font-display text-sm font-bold text-[#0B2F22]">
          {number}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" strokeWidth={1.9} />
            <h3 className="font-display text-lg font-semibold text-primary">{title}</h3>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

export function HouseholdProductStoryEditor({
  form,
  onDetailChange,
  onCollectionItemChange,
  onAddCollectionItem,
  onRemoveCollectionItem,
}) {
  const details = form.householdDetails;
  const impactMetrics = details.impactMetrics || [];
  const waterImpactMetrics = details.waterImpactMetrics || [];
  const usageSteps = details.usageSteps || [];

  return (
    <fieldset className="sm:col-span-2 lg:col-span-12 overflow-hidden rounded-[1.75rem] border border-primary/20 bg-background p-4 shadow-soft sm:p-6">
      <legend className="sr-only">Háztartási termékoldal szerkesztése</legend>
      <div className="mb-7 border-b border-primary/10 pb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8A6A22]">
          Háztartási kategória
        </p>
        <h2 className="mt-2 font-display text-2xl font-semibold text-primary">
          Termékoldal története és struktúrája
        </h2>
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-muted-foreground">
          A mintaképek alapján felépített blokkok ebben a sorrendben jelennek meg. Az üresen hagyott részek automatikusan kimaradnak.
        </p>
      </div>

      <div className="space-y-4">
        <HouseholdEditorSection
          number="01"
          icon={Package}
          title="Gyors termékadatok"
          description="Kiszerelés, használat, környezeti jellemzők és biztonsági tudnivalók."
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Kiszerelés</label>
              <input value={details.packSize} onChange={(event) => onDetailChange('householdDetails', 'packSize', event.target.value)} className={inputClass} placeholder="pl. 85 g / 750 ml" />
            </div>
            <div>
              <label className={labelClass}>Illat</label>
              <input value={details.scent} onChange={(event) => onDetailChange('householdDetails', 'scent', event.target.value)} className={inputClass} placeholder="pl. édesnarancs / illatmentes" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Mire használható? (vesszővel)</label>
              <input value={details.suitableFor} onChange={(event) => onDetailChange('householdDetails', 'suitableFor', event.target.value)} className={inputClass} placeholder="pl. zsíros edények, ráégett maradék, utazás" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Adagolás / általános használat</label>
              <textarea value={details.dosage} onChange={(event) => onDetailChange('householdDetails', 'dosage', event.target.value)} className={`${inputClass} min-h-24 resize-y`} placeholder="Mennyit és hogyan használjon a vásárló?" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Környezetbarát jellemzők (vesszővel)</label>
              <input value={details.ecoFeatures} onChange={(event) => onDetailChange('householdDetails', 'ecoFeatures', event.target.value)} className={inputClass} placeholder="pl. pálmaolajmentes, vegán, minimális csomagolás" />
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className={labelClass}>Figyelmeztetések</label>
              <textarea value={details.warnings} onChange={(event) => onDetailChange('householdDetails', 'warnings', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Biztonsági és tárolási tudnivalók" />
            </div>
          </div>
        </HouseholdEditorSection>

        <HouseholdEditorSection
          number="02"
          icon={BarChart3}
          title="Környezeti hatás és megtakarítás"
          description="A víz- és a műanyag-megtakarítási blokk külön-külön bekapcsolható, ezért ugyanazon a termékoldalon mindkettő megjelenhet."
        >
          <div className="grid gap-3 lg:grid-cols-2">
            <ImpactToggle
              enabled={details.waterImpactEnabled}
              onChange={(enabled) => onDetailChange('householdDetails', 'waterImpactEnabled', enabled)}
              color={details.waterImpactColor}
              icon={Droplets}
              title="Kevesebb szállított víz"
              description="Víztartalom-összehasonlítás, szállítási adatok és kiemelt összegzés."
            />
            <ImpactToggle
              enabled={details.plasticImpactEnabled}
              onChange={(enabled) => onDetailChange('householdDetails', 'plasticImpactEnabled', enabled)}
              color={details.plasticImpactColor}
              icon={Recycle}
              title="Kevesebb eldobott műanyag"
              description="Flakonmegtakarítás, százalékos ábra és hosszabb távú környezeti hatás."
            />
          </div>

          {details.waterImpactEnabled ? (
            <div className="mt-5 rounded-2xl border border-primary/10 bg-secondary/25 p-4 sm:p-5">
              <div className="mb-4 flex items-center gap-2">
                <Droplets className="h-4 w-4" style={{ color: details.waterImpactColor }} />
                <h4 className="font-display text-base font-semibold text-primary">Víz- és szállítási blokk</h4>
              </div>
              <ColorPaletteField
                label="Vízblokk kiemelőszíne"
                value={details.waterImpactColor}
                onChange={(color) => onDetailChange('householdDetails', 'waterImpactColor', color)}
                hint="A vízoszlopok, ikonok, kiemelések és az összegző sáv színe."
              />
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div><label className={labelClass}>Blokk címe</label><input value={details.waterImpactTitle} onChange={(event) => onDetailChange('householdDetails', 'waterImpactTitle', event.target.value)} className={inputClass} placeholder="pl. Mennyivel kevesebb vizet szállítasz?" /></div>
                <div><label className={labelClass}>Bevezető</label><textarea value={details.waterImpactIntro} onChange={(event) => onDetailChange('householdDetails', 'waterImpactIntro', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Röviden mutasd be az összehasonlítás alapját." /></div>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div><label className={labelClass}>Átlagos termék víztartalma (%)</label><input type="number" min="0" max="100" value={details.averageWaterPercentage} onChange={(event) => onDetailChange('householdDetails', 'averageWaterPercentage', event.target.value)} className={inputClass} placeholder="pl. 85" /></div>
                <div><label className={labelClass}>Ennek a terméknek a víztartalma (%)</label><input type="number" min="0" max="100" value={details.productWaterPercentage} onChange={(event) => onDetailChange('householdDetails', 'productWaterPercentage', event.target.value)} className={inputClass} placeholder="pl. 0" /></div>
                <div className="sm:col-span-2"><label className={labelClass}>Víztartalom magyarázata</label><textarea value={details.waterComparisonText} onChange={(event) => onDetailChange('householdDetails', 'waterComparisonText', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Mit jelent a két százalék közötti különbség?" /></div>
                <div className="sm:col-span-2"><label className={labelClass}>Szállítási kiemelés</label><input value={details.transportCostHighlight} onChange={(event) => onDetailChange('householdDetails', 'transportCostHighlight', event.target.value)} className={inputClass} placeholder="pl. Vizet fuvaroztatsz – literével" /></div>
                <div className="sm:col-span-2"><label className={labelClass}>Szállítási magyarázat</label><textarea value={details.transportCostText} onChange={(event) => onDetailChange('householdDetails', 'transportCostText', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Hogyan csökkenti a koncentrált forma a szállított tömeget?" /></div>
              </div>
              <div className="mt-4 space-y-3">
                <p className={labelClass}>Szállítási számok és összehasonlítások</p>
                {waterImpactMetrics.map((metric, index) => (
                  <div key={`water-impact-${index}`} className="rounded-2xl border border-primary/10 bg-card p-4">
                    <div className="mb-3 flex items-center justify-between"><span className="text-xs font-semibold" style={{ color: details.waterImpactColor }}>{index + 1}. adat</span><button type="button" onClick={() => onRemoveCollectionItem('householdDetails', 'waterImpactMetrics', index)} className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-primary/10 hover:text-primary" aria-label={`${index + 1}. víz- és szállítási adat törlése`}><Trash2 className="h-3.5 w-3.5" /></button></div>
                    <div className="grid gap-3 md:grid-cols-[0.8fr_0.45fr_1fr]">
                      <div><label className={labelClass}>Megnevezés</label><input value={metric.label} onChange={(event) => onCollectionItemChange('householdDetails', 'waterImpactMetrics', index, 'label', event.target.value)} className={inputClass} placeholder="pl. Víz / flakon" /></div>
                      <div><label className={labelClass}>Érték</label><input value={metric.value} onChange={(event) => onCollectionItemChange('householdDetails', 'waterImpactMetrics', index, 'value', event.target.value)} className={inputClass} placeholder="pl. ~425 g" /></div>
                      <div><label className={labelClass}>Magyarázat</label><input value={metric.note} onChange={(event) => onCollectionItemChange('householdDetails', 'waterImpactMetrics', index, 'note', event.target.value)} className={inputClass} /></div>
                    </div>
                  </div>
                ))}
                {waterImpactMetrics.length < 4 ? <button type="button" onClick={() => onAddCollectionItem('householdDetails', 'waterImpactMetrics', { label: '', value: '', barPercentage: '', note: '' }, 4)} className="flex min-h-20 w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-primary/25 bg-card text-xs font-medium text-primary hover:bg-primary/[0.04]"><Plus className="h-4 w-4" /> Szállítási adat hozzáadása</button> : null}
              </div>
              <div className="mt-4"><label className={labelClass}>Kiemelt összegzés</label><textarea value={details.waterImpactCallout} onChange={(event) => onDetailChange('householdDetails', 'waterImpactCallout', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Egy könnyen elképzelhető összehasonlítás a víz szállításáról." /></div>
            </div>
          ) : null}

          {details.plasticImpactEnabled ? (
            <div className="mt-5 rounded-2xl border border-primary/10 bg-secondary/25 p-4 sm:p-5">
              <div className="mb-4 flex items-center gap-2">
                <Recycle className="h-4 w-4" style={{ color: details.plasticImpactColor }} />
                <h4 className="font-display text-base font-semibold text-primary">Műanyag-megtakarítási blokk</h4>
              </div>
              <ColorPaletteField
                label="Műanyagblokk kiemelőszíne"
                value={details.plasticImpactColor}
                onChange={(color) => onDetailChange('householdDetails', 'plasticImpactColor', color)}
                hint="A kördiagram, aránysávok, ikonok és kiemelések színe."
              />
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div><label className={labelClass}>Blokk címe</label><input value={details.plasticImpactTitle} onChange={(event) => onDetailChange('householdDetails', 'plasticImpactTitle', event.target.value)} className={inputClass} placeholder="pl. Mennyi műanyag eldobását spórolod meg?" /></div>
                <div><label className={labelClass}>Bevezető</label><textarea value={details.plasticImpactIntro} onChange={(event) => onDetailChange('householdDetails', 'plasticImpactIntro', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Röviden mutasd be a számítás alapját." /></div>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div><label className={labelClass}>Műanyag mennyisége</label><input value={details.plasticAmount} onChange={(event) => onDetailChange('householdDetails', 'plasticAmount', event.target.value)} className={inputClass} placeholder="pl. 480 g / év" /></div>
                <div><label className={labelClass}>Megspórolt arány (%)</label><input type="number" min="0" max="100" value={details.plasticPercentage} onChange={(event) => onDetailChange('householdDetails', 'plasticPercentage', event.target.value)} className={inputClass} placeholder="pl. 97" /></div>
                <div className="sm:col-span-2"><label className={labelClass}>Szemléletes egyenérték</label><input value={details.plasticEquivalent} onChange={(event) => onDetailChange('householdDetails', 'plasticEquivalent', event.target.value)} className={inputClass} placeholder="pl. Évente körülbelül 12 műanyag flakon" /></div>
                <div className="sm:col-span-2 lg:col-span-4"><label className={labelClass}>Környezeti hatás magyarázata</label><textarea value={details.plasticImpactText} onChange={(event) => onDetailChange('householdDetails', 'plasticImpactText', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Röviden, tényszerűen magyarázd el a műanyag környezeti terhét." /></div>
              </div>
              <div className="mt-4 space-y-3">
                {impactMetrics.map((metric, index) => (
                  <div key={`impact-${index}`} className="rounded-2xl border border-primary/10 bg-card p-4">
                    <div className="mb-3 flex items-center justify-between"><span className="text-xs font-semibold" style={{ color: details.plasticImpactColor }}>{index + 1}. összehasonlító adat</span><button type="button" onClick={() => onRemoveCollectionItem('householdDetails', 'impactMetrics', index)} className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-primary/10 hover:text-primary" aria-label={`${index + 1}. műanyagadat törlése`}><Trash2 className="h-3.5 w-3.5" /></button></div>
                    <div className="grid gap-3 md:grid-cols-[0.8fr_0.4fr_0.4fr_1fr]">
                      <div><label className={labelClass}>Megnevezés</label><input value={metric.label} onChange={(event) => onCollectionItemChange('householdDetails', 'impactMetrics', index, 'label', event.target.value)} className={inputClass} placeholder="pl. Hagyományos mosogatószer" /></div>
                      <div><label className={labelClass}>Érték</label><input value={metric.value} onChange={(event) => onCollectionItemChange('householdDetails', 'impactMetrics', index, 'value', event.target.value)} className={inputClass} placeholder="pl. 12 flakon / év" /></div>
                      <div><label className={labelClass}>Sáv (%)</label><input type="number" min="0" max="100" value={metric.barPercentage} onChange={(event) => onCollectionItemChange('householdDetails', 'impactMetrics', index, 'barPercentage', event.target.value)} className={inputClass} /></div>
                      <div><label className={labelClass}>Magyarázat</label><input value={metric.note} onChange={(event) => onCollectionItemChange('householdDetails', 'impactMetrics', index, 'note', event.target.value)} className={inputClass} /></div>
                    </div>
                  </div>
                ))}
                {impactMetrics.length < 4 ? <button type="button" onClick={() => onAddCollectionItem('householdDetails', 'impactMetrics', { label: '', value: '', barPercentage: '', note: '' }, 4)} className="flex min-h-20 w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-primary/25 bg-card text-xs font-medium text-primary hover:bg-primary/[0.04]"><Plus className="h-4 w-4" /> Összehasonlító adat hozzáadása</button> : null}
              </div>
              <div className="mt-4"><label className={labelClass}>„Gondolj bele” kiemelés</label><textarea value={details.plasticImpactCallout} onChange={(event) => onDetailChange('householdDetails', 'plasticImpactCallout', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Egy könnyen elképzelhető összehasonlítás a megspórolt flakonokról." /></div>
            </div>
          ) : null}
        </HouseholdEditorSection>

        <HouseholdEditorSection
          number="03"
          icon={CircleOff}
          title="Ami nincs benne, az is számít"
          description="A termék összetételét és a tudatosan elhagyott anyagokat bemutató blokk."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className={labelClass}>Blokk címe</label><input value={details.compositionTitle} onChange={(event) => onDetailChange('householdDetails', 'compositionTitle', event.target.value)} className={inputClass} placeholder="pl. Ami nincs benne, az is számít" /></div>
            <div><label className={labelClass}>Rövid bevezető</label><textarea value={details.compositionIntro} onChange={(event) => onDetailChange('householdDetails', 'compositionIntro', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Rövid összefoglaló az összetételről." /></div>
            <div className="sm:col-span-2"><label className={labelClass}>Nem tartalmazza (vesszővel)</label><input value={details.excludedIngredients} onChange={(event) => onDetailChange('householdDetails', 'excludedIngredients', event.target.value)} className={inputClass} placeholder="pl. műanyag flakon, pálmaolaj, állati összetevő" /></div>
          </div>
        </HouseholdEditorSection>

        <HouseholdEditorSection
          number="04"
          icon={ListChecks}
          title="Használat lépésről lépésre"
          description="Legfeljebb négy rövid, számozott lépés sötétzöld bemutatóblokkban."
        >
          <div><label className={labelClass}>Blokk címe</label><input value={details.usageTitle} onChange={(event) => onDetailChange('householdDetails', 'usageTitle', event.target.value)} className={inputClass} placeholder="pl. Szappanból ragyogó edény, négy lépésben" /></div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {usageSteps.map((step, index) => (
              <div key={`step-${index}`} className="rounded-2xl border border-primary/10 bg-secondary/45 p-4">
                <div className="mb-3 flex items-center justify-between"><span className="text-xs font-semibold text-[#B28D2F]">{String(index + 1).padStart(2, '0')}. lépés</span><button type="button" onClick={() => onRemoveCollectionItem('householdDetails', 'usageSteps', index)} className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-primary/10 hover:text-primary" aria-label={`${index + 1}. lépés törlése`}><Trash2 className="h-3.5 w-3.5" /></button></div>
                <label className={labelClass}>Cím</label><input value={step.title} onChange={(event) => onCollectionItemChange('householdDetails', 'usageSteps', index, 'title', event.target.value)} className={inputClass} placeholder="pl. Nedvesítsd be" />
                <label className={`${labelClass} mt-3`}>Magyarázat</label><textarea value={step.description} onChange={(event) => onCollectionItemChange('householdDetails', 'usageSteps', index, 'description', event.target.value)} className={`${inputClass} min-h-20 resize-y`} placeholder="Rövid használati útmutató" />
              </div>
            ))}
            {usageSteps.length < 4 ? <button type="button" onClick={() => onAddCollectionItem('householdDetails', 'usageSteps', { title: '', description: '' }, 4)} className="flex min-h-44 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-primary/25 bg-primary/[0.035] text-primary hover:bg-primary/[0.07]"><Plus className="h-5 w-5" /><span className="text-xs font-medium">Lépés hozzáadása</span></button> : null}
          </div>
        </HouseholdEditorSection>

      </div>
    </fieldset>
  );
}
